import { FormHandles } from '@unform/core';
import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { format } from 'date-fns';

import { InputText } from '../../../../components/InputText';
import { Painel } from '../../../../components/Painel';
import { Icon } from '../../../../components/Icon';

import { api } from '../../../../services/api';
import { colors } from '../../../../styles/tokens';
import { useToast } from '../../../../hooks/toast';
import { formatName } from '../../../../utils/formatName';

import noAvatar from '../../../../assets/no-avatar.svg';

import {
  BackLink,
  Card,
  Container,
  ContainerAvatar,
  Form,
  Header,
  HeaderInfo,
  ImcBox,
  ImcField,
  ImcTag,
  Row2,
  Row4,
  Save,
  Separate,
  StudentName,
  Subtitle,
} from './styles';

interface IUserProps {
  fullname: string;
  avatar_url?: string;
}

interface IMeasurementsDTO {
  height: number | null;
  weight: number | null;
  goal: string | null;
  neck: number | null;
  shoulder: number | null;
  chest: number | null;
  waist: number | null;
  abdomen: number | null;
  hip: number | null;
  left_bicep: number | null;
  right_bicep: number | null;
  left_forearm: number | null;
  right_forearm: number | null;
  left_thigh: number | null;
  right_thigh: number | null;
  left_calf: number | null;
  right_calf: number | null;
  updated_at?: string;
}

const BODY_FIELDS: { key: keyof IMeasurementsDTO; label: string }[] = [
  { key: 'neck', label: 'Pescoço' },
  { key: 'shoulder', label: 'Ombro' },
  { key: 'chest', label: 'Peito' },
  { key: 'waist', label: 'Cintura' },
  { key: 'abdomen', label: 'Abdômen' },
  { key: 'hip', label: 'Quadril' },
  { key: 'left_bicep', label: 'Bíceps esquerdo' },
  { key: 'right_bicep', label: 'Bíceps direito' },
  { key: 'left_forearm', label: 'Antebraço esquerdo' },
  { key: 'right_forearm', label: 'Antebraço direito' },
  { key: 'left_thigh', label: 'Coxa esquerda' },
  { key: 'right_thigh', label: 'Coxa direita' },
  { key: 'left_calf', label: 'Panturrilha esquerda' },
  { key: 'right_calf', label: 'Panturrilha direita' },
];

function classifyImc(imc: number): string {
  if (imc < 18.5) return 'Abaixo do peso';
  if (imc < 25) return 'Peso normal';
  if (imc < 30) return 'Sobrepeso';
  return 'Obesidade';
}

const UserMeasurements: React.FC = () => {
  const formRef = useRef<FormHandles>(null);
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [user, setUser] = useState<IUserProps>();
  const [measurements, setMeasurements] = useState<IMeasurementsDTO | null>(
    null,
  );

  // Altura/peso são campos do unform (não controlados por React) --
  // esse par de estados existe só pra recalcular o IMC ao vivo enquanto
  // o usuário digita, sem depender de `measurements` (que só muda no
  // load inicial e depois de salvar).
  const [liveHeight, setLiveHeight] = useState<number | null>(null);
  const [liveWeight, setLiveWeight] = useState<number | null>(null);

  const getUserData = useCallback(() => {
    api
      .get(`users/gym/${id}`)
      .then(response => setUser(response.data.user))
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca dos dados do usuário.',
          description: err?.response?.status,
        });
      });
  }, [addToast, id]);

  const getMeasurements = useCallback(() => {
    setLoading(true);

    api
      .get(`measurements/${id}`)
      .then(response => setMeasurements(response.data))
      .catch(err => {
        addToast({
          type: 'error',
          title: 'Erro na busca das medidas.',
          description: err?.response?.status,
        });
      })
      .finally(() => setLoading(false));
  }, [addToast, id]);

  useEffect(() => {
    getUserData();
    getMeasurements();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setLiveHeight(measurements?.height ?? null);
    setLiveWeight(measurements?.weight ?? null);
  }, [measurements]);

  const handleSubmit = useCallback(
    async (data: Record<string, string | number>) => {
      try {
        setSaving(true);

        const response = await api.put(`measurements/${id}`, data);

        setMeasurements(response.data);

        addToast({
          type: 'success',
          title: 'Medidas atualizadas com sucesso.',
        });
      } catch (err: any) {
        addToast({
          type: 'error',
          title: 'Erro ao salvar as medidas',
          description: err?.response?.status,
        });
      } finally {
        setSaving(false);
      }
    },
    [addToast, id],
  );

  const imc = useMemo(() => {
    if (!liveHeight || !liveWeight) return null;

    return liveWeight / (liveHeight / 100) ** 2;
  }, [liveHeight, liveWeight]);

  return (
    <Painel loading={loading}>
      <Container>
        <BackLink type="button" onClick={() => navigate(`/users/${id}`)}>
          <Icon name="arrow_back" size={16} color={colors.primary} />
          Detalhes do aluno
        </BackLink>

        <Header>
          <HeaderInfo>
            <ContainerAvatar>
              <img src={user?.avatar_url || noAvatar} alt="avatar do aluno" />
            </ContainerAvatar>
            <div>
              <StudentName>
                Medidas de <span>{formatName(user?.fullname || '')}</span>
              </StudentName>
              <Subtitle>
                Última atualização:{' '}
                <strong>
                  {measurements?.updated_at
                    ? format(new Date(measurements.updated_at), 'dd/MM/yyyy')
                    : '--/--/----'}
                </strong>
              </Subtitle>
            </div>
          </HeaderInfo>

          <Save
            type="button"
            disabled={saving}
            onClick={() => formRef.current?.submitForm()}
          >
            Salvar Medidas
          </Save>
        </Header>

        <Form
          ref={formRef}
          initialData={measurements || {}}
          onSubmit={handleSubmit}
        >
          <Card>
            <Separate label="Dados Gerais" />
            <Row4>
              <InputText
                label="Altura (cm)"
                name="height"
                type="number"
                onChange={e =>
                  setLiveHeight(e.target.value ? Number(e.target.value) : null)
                }
              />
              <InputText
                label="Peso (kg)"
                name="weight"
                type="number"
                onChange={e =>
                  setLiveWeight(e.target.value ? Number(e.target.value) : null)
                }
              />

              <ImcField>
                <span>IMC (calculado)</span>
                <ImcBox>
                  <strong>
                    {imc !== null ? imc.toFixed(1).replace('.', ',') : '—'}
                  </strong>
                  {imc !== null && <ImcTag>{classifyImc(imc)}</ImcTag>}
                </ImcBox>
              </ImcField>

              <InputText label="Objetivo" name="goal" />
            </Row4>
          </Card>

          <Card>
            <Separate label="Medidas Corporais (cm)" />
            <Row2>
              {BODY_FIELDS.map(field => (
                <InputText
                  key={field.key}
                  label={field.label}
                  name={field.key}
                  type="number"
                />
              ))}
            </Row2>
          </Card>
        </Form>
      </Container>
    </Painel>
  );
};

export { UserMeasurements };
